import React, { createContext, useContext, useState, useCallback } from 'react';
import en from '../i18n/en.json';
import fr from '../i18n/fr.json';

type Lang = 'EN' | 'FR';

const translations: Record<Lang, Record<string, unknown>> = { EN: en, FR: fr };

function resolveDotPath(obj: Record<string, unknown>, path: string): string {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return path;
    current = (current as Record<string, unknown>)[part];
  }
  if (typeof current === 'string') return current;
  return path;
}

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: 'EN',
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = sessionStorage.getItem('app_lang');
    return (saved === 'FR' ? 'FR' : 'EN') as Lang;
  });

  const setLangPersist = useCallback((l: Lang) => {
    sessionStorage.setItem('app_lang', l);
    setLang(l);
  }, []);

  const t = useCallback(
    (key: string): string => resolveDotPath(translations[lang] as Record<string, unknown>, key),
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang: setLangPersist, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
